/**
 * net.ts — the client's only connection to the server.
 *
 * Carries reliable events (join/leave) and the per-tick snapshot stream. Clients
 * send inputs only; they never send positions or outcomes (AGENTS.md: server-authoritative).
 *
 * The `?lag=` debug param routes both directions through a delay queue so
 * interpolation can be checked without a real bad connection. The delay is a
 * round trip, split evenly between send and receive.
 */

import { Client, type Room } from 'colyseus.js';
import {
  MSG,
  ROOM_NAME,
  activeCodec,
  type ChatMsg,
  type CombatMsg,
  type InputCommand,
  type PlayerJoinedMsg,
  type PlayerLeftMsg,
  type RoundMsg,
  type Snapshot,
  type SnapshotMsg,
  type SayMsg,
  type SetLoadoutMsg,
  type TuneMsg,
  type TunedMsg,
  type WelcomeMsg,
} from '@dueler/shared';

/** Dev runs the server on its own port; production serves client and WS together. */
function serverEndpoint(): string {
  const override = import.meta.env['VITE_SERVER_URL'];
  if (typeof override === 'string' && override.length > 0) return override;
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return import.meta.env.DEV
    ? `${scheme}://${location.hostname}:2567`
    : `${scheme}://${location.host}`;
}

export interface SessionHandlers {
  onWelcome(msg: WelcomeMsg): void;
  onPlayerJoined(msg: PlayerJoinedMsg): void;
  onPlayerLeft(msg: PlayerLeftMsg): void;
  onSnapshot(snapshot: Snapshot): void;
  /** Hits, blocks, parries, kills and respawns for one tick. */
  onCombat(msg: CombatMsg): void;
  /** The round phase or the scores changed. Also arrives once, on join. */
  onRound(msg: RoundMsg): void;
  /** Someone said something in room chat. */
  onChat(msg: ChatMsg): void;
  /** A feel constant changed on the server; mirror it locally. */
  onTuned(msg: TunedMsg): void;
  /** Connection dropped after a successful join. */
  onDisconnect(code: number): void;
}

export interface Session {
  room: Room;
  sendInput(command: InputCommand): void;
  /** Asks the server to change a feel constant (debug tuning panel). */
  sendTune(msg: TuneMsg): void;
  /** Asks the server to wear a loadout. The server decides and broadcasts. */
  sendLoadout(msg: SetLoadoutMsg): void;
  /** Says something in room chat. The server sanitizes it and attributes it. */
  sendSay(msg: SayMsg): void;
  leave(): void;
}

export interface JoinConfig {
  code: string;
  name: string;
  /** Simulated round-trip latency, ms. 0 disables the delay queue entirely. */
  lagMs: number;
}

/** Runs `fn` after the one-way share of the simulated latency. */
function makeDelayer(lagMs: number): (fn: () => void) => void {
  if (lagMs <= 0) return (fn) => fn();
  const oneWay = lagMs / 2;
  return (fn) => {
    setTimeout(fn, oneWay);
  };
}

/**
 * Joins the room with this code, creating it if nobody is in it yet. Rejects with
 * a `JoinErrorCode` message (BAD_CODE / ROOM_FULL) if the server refuses.
 */
export async function joinRoom(config: JoinConfig, handlers: SessionHandlers): Promise<Session> {
  const client = new Client(serverEndpoint());
  const room = await client.joinOrCreate(ROOM_NAME, { code: config.code, name: config.name });
  const delay = makeDelayer(config.lagMs);

  room.onMessage<WelcomeMsg>(MSG.Welcome, (msg) => delay(() => handlers.onWelcome(msg)));
  room.onMessage<PlayerJoinedMsg>(MSG.PlayerJoined, (msg) => delay(() => handlers.onPlayerJoined(msg)));
  room.onMessage<PlayerLeftMsg>(MSG.PlayerLeft, (msg) => delay(() => handlers.onPlayerLeft(msg)));
  room.onMessage<SnapshotMsg>(MSG.Snapshot, (msg) => {
    delay(() => {
      if (msg.v !== activeCodec.version) {
        console.warn(`[net] snapshot codec v${msg.v}, expected v${activeCodec.version}`);
        return;
      }
      handlers.onSnapshot(activeCodec.decode(msg.w));
    });
  });
  room.onMessage<CombatMsg>(MSG.Combat, (msg) => delay(() => handlers.onCombat(msg)));
  room.onMessage<RoundMsg>(MSG.Round, (msg) => delay(() => handlers.onRound(msg)));
  room.onMessage<ChatMsg>(MSG.Chat, (msg) => delay(() => handlers.onChat(msg)));
  room.onMessage<TunedMsg>(MSG.Tuned, (msg) => delay(() => handlers.onTuned(msg)));
  room.onLeave((leaveCode) => handlers.onDisconnect(leaveCode));

  return {
    room,
    sendInput: (command) => delay(() => room.send(MSG.Input, command)),
    sendTune: (msg) => delay(() => room.send(MSG.Tune, msg)),
    sendLoadout: (msg) => delay(() => room.send(MSG.SetLoadout, msg)),
    sendSay: (msg) => delay(() => room.send(MSG.Say, msg)),
    leave: () => void room.leave(true),
  };
}
